import { AnchorProvider } from "@coral-xyz/anchor";
import {
  JET_MARKET_ADDRESS,
  JET_MARKET_ADDRESS_DEVNET,
  JetMarket,
  JetObligation,
  JetReserve,
  JetUser,
} from "@jet-lab/jet-engine";
import { JetClient } from "@jet-lab/jet-engine/dist/cjs/pools/client";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Cluster, Connection, PublicKey } from "@solana/web3.js";
import { convert, readOnlyAnchorWallet } from "@stepfinance/common";
import type { LendingCollateralPosition, LendingCollateralPositionMap } from "@stepfinance/lending";
import { useEffect, useState } from "react";

import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useMarkets } from "../../contexts/market";
import { getTokenByMint } from "../../utils/tokenInfo";

const getJetPositions = async (
  connection: Connection,
  network: Cluster,
  publicKey: PublicKey,
): Promise<LendingCollateralPositionMap> => {
  const collateralPositionMap: LendingCollateralPositionMap = {};

  const provider = new AnchorProvider(connection, readOnlyAnchorWallet, {
    commitment: "recent",
  });
  const client = await JetClient.connect(provider, true);
  const marketAddress = network === "mainnet-beta" ? JET_MARKET_ADDRESS : JET_MARKET_ADDRESS_DEVNET;
  const market = await JetMarket.load(client, marketAddress);
  const reserves = await JetReserve.loadMultiple(client, market);
  const user = await JetUser.load(client, market, reserves, publicKey);
  const obligation = JetObligation.create(
    market,
    user,
    reserves.map((reserve) => reserve.data),
  );

  obligation.positions.forEach((position) => {
    const mintAddress = position.reserve.tokenMint.toBase58();

    const token = getTokenByMint(position.reserve.tokenMint.toBase58());
    if (token) {
      const collateralPosition: LendingCollateralPosition = {
        deposit: {
          amount: 0,
          apy: position.reserve.depositApy,
        },
        borrow: {
          amount: 0,
          apy: position.reserve.borrowApr,
        },
        display: true,
      };

      if (!position.collateralBalance.isZero()) {
        collateralPosition.deposit.amount = convert(position.collateralBalance.lamports, token.decimals);
        collateralPositionMap[mintAddress] = collateralPosition;
      }

      if (!position.loanBalance.isZero()) {
        collateralPosition.borrow.amount = convert(position.loanBalance.lamports, token.decimals);
        collateralPositionMap[mintAddress] = collateralPosition;
      }
    } else {
      console.warn("Cannot find token for mint: ", mintAddress);
    }
  });

  return collateralPositionMap;
};

export const useJet = () => {
  const { marketsInitialised, refreshing } = useMarkets();
  const { connected, publicKey } = useEnrichedWallet();
  const { network } = useNetworkConfig();
  const { connection } = useConnection();
  const [collateralPositionMap, setCollateralPositionMap] = useState<LendingCollateralPositionMap>({});

  useEffect(() => setCollateralPositionMap({}), [connected, publicKey]);

  useEffect(() => {
    const marketsRefreshFinished = !refreshing;
    if (connected && publicKey && marketsInitialised && marketsRefreshFinished) {
      void getJetPositions(connection, network, publicKey).then((jetPositions) => {
        setCollateralPositionMap(jetPositions);
      });
    }
  }, [connected, connection, marketsInitialised, network, publicKey, refreshing]);

  return { collateral: collateralPositionMap, yieldFarms: {} };
};
